/**
 * Health, and being hurt.
 *
 * The first thing in this world that can be damaged, and the foundation the
 * raids need: orcs, defences, weapons and armour all reduce to "something has
 * hit something and it mattered". Until now the sabre and the arrows landed
 * and nothing came of it.
 *
 * Pure arithmetic and a clock. Nothing here knows what a player is, what an orc
 * is, or how a hit is drawn.
 */

/** Twenty, so a point is worth having and half of one is not needed. */
export const PLAYER_MAX_HP = 20;

/**
 * Seconds of grace after a hit lands.
 *
 * Without it a swing that overlaps two frames hits twice, an arrow resting
 * inside somebody kills them, and two sources at once stack instead of the
 * worse one winning. Every game with melee has this rule and it is not
 * decoration.
 */
export const HIT_GRACE = 0.5;

/**
 * Seconds a body shows that it was hit.
 *
 * Its own timer rather than `grace`, which is half a second and is a rule
 * about what may land next. Tying the flash to it would mean that tuning the
 * fighting silently retimes the flinch, and that a body which cannot be hit
 * again is a body still lit up.
 */
export const HURT_FLASH = 0.25;

/** Seconds of not being hurt before the body starts mending. */
export const HEAL_DELAY = 6;
/** Seconds per point regained after that. */
export const HEAL_INTERVAL = 2;

export class Vitals {
  hp: number;
  readonly max: number;
  /** Seconds of grace left. Nothing can land while this is running. */
  grace = 0;
  /** Seconds left of showing that a blow landed. See HURT_FLASH. */
  flash = 0;
  /** Seconds since the last hit, for deciding when mending starts. */
  private sinceHurt = HEAL_DELAY;
  private mending = 0;

  constructor(max: number) {
    this.max = max;
    this.hp = max;
  }

  get dead(): boolean {
    return this.hp <= 0;
  }

  get fraction(): number {
    return this.max <= 0 ? 0 : Math.max(0, this.hp) / this.max;
  }

  /**
   * Take a hit.
   *
   * @returns how much was actually taken, so a caller can tell a landed blow
   * from one that arrived during the grace period - a hit that does nothing
   * should not make a noise or throw the victim about.
   */
  hurt(amount: number): number {
    if (amount <= 0 || this.grace > 0 || this.dead) return 0;
    const taken = Math.min(amount, this.hp);
    this.hp -= taken;
    this.grace = HIT_GRACE;
    this.flash = HURT_FLASH;
    this.sinceHurt = 0;
    this.mending = 0;
    return taken;
  }

  /**
   * Show a blow without taking one.
   *
   * For a body whose health is somebody else's business: an orc mirrored off
   * the server has hit points that only the server keeps, but it still has to
   * flash white when it is hit or nobody can tell their sabre is landing.
   */
  showBlow(): void {
    if (this.dead) return;
    this.flash = HURT_FLASH;
  }

  heal(amount: number): void {
    if (this.dead) return;
    this.hp = Math.min(this.max, this.hp + amount);
  }

  /** Back to full, as after a death. */
  revive(): void {
    this.hp = this.max;
    this.grace = HIT_GRACE;
    this.flash = 0;
    this.sinceHurt = HEAL_DELAY;
    this.mending = 0;
  }

  /**
   * Run the clocks.
   *
   * Mending is slow and stops the moment anything lands, which is what makes
   * running away a decision rather than a formality. There is no food in this
   * game, so a body that never mends is a body that only ever gets worse.
   */
  /**
   * How brightly a blow is showing, 1 down to 0.
   *
   * Read by whatever is drawing the body. Given as a fraction rather than as
   * seconds so the drawing does not have to know how long a flash lasts.
   */
  get glow(): number {
    return HURT_FLASH <= 0 ? 0 : Math.max(0, this.flash) / HURT_FLASH;
  }

  step(dt: number): void {
    if (this.grace > 0) this.grace = Math.max(0, this.grace - dt);
    if (this.flash > 0) this.flash = Math.max(0, this.flash - dt);
    if (this.dead) return;

    this.sinceHurt += dt;
    if (this.sinceHurt < HEAL_DELAY || this.hp >= this.max) return;

    this.mending += dt;
    while (this.mending >= HEAL_INTERVAL && this.hp < this.max) {
      this.mending -= HEAL_INTERVAL;
      this.hp += 1;
    }
  }
}

/**
 * What a fall costs.
 *
 * Measured in the speed you land at rather than the height you fell from: the
 * mover already knows the one and would have to be told to remember the other,
 * and water and a slope both change what a given height is worth.
 *
 * The free drop is generous. Missing a step should never hurt, and this game
 * has step-up, ledges and boats to come.
 */
/**
 * What лава costs, per blow, while you are standing in it.
 *
 * Blows land no oftener than the grace period allows, so this is six сила a
 * second and a little over three seconds of life. Long enough that a step
 * into a pool is a scramble back out and not simply a death, short enough
 * that swimming a lava lake is never the way round anything.
 */
export const LAVA_DAMAGE = 3;

export const SAFE_LANDING_SPEED = 13;
/** Points per unit of speed above the free drop. */
export const FALL_DAMAGE_PER_SPEED = 1.4;

export function fallDamage(impactSpeed: number): number {
  const over = impactSpeed - SAFE_LANDING_SPEED;
  if (over <= 0) return 0;
  return Math.round(over * FALL_DAMAGE_PER_SPEED);
}
